import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import { useMemo, useCallback, useState, useEffect } from 'react';
import * as React from 'react';
import { usePayment } from '../providers/PaymentProvider';
import { SelectedCollectionProps, useSelectedCollectionDetails } from '../providers/SelectedCollectionDataProvider';
import { useSelectedCollection } from '../providers/SelectedCollectionProvider';
import { formatAddress } from '../utils/formatAddress';
import { getUniqueDiscounts } from '../utils/getUniqueDiscounts.utils';
import { JSON_CONTENT } from '@/assets/json';

const ChooseCollection = () => {
  const theme = useTheme();
  const [metaDataJSON, setMetaDataJSON] = useState<any[]>([]);
  const { selectedCollection, setSelectedCollection } = useSelectedCollection();
  const { metaDataList } = usePayment();
  const { setSelectedCollectionData } = useSelectedCollectionDetails();

  const [isErrorFree, setIsErrorFree] = useState(false);
  const [isErrorDiscount, setIsErrorDiscount] = useState(false);
  const [isErrorDiscountTwo, setIsErrorDiscountTwo] = useState(false);

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        const response = await fetch(JSON_CONTENT.META_DATA);
        const data = await response.json();
        setMetaDataJSON(data);
      } catch (error) {
        console.error('Error fetching metadata:', error);
      }
    };
    fetchMetaData();
  }, []);

  const showFree = useMemo(() => {
    const data = metaDataList.filter((item) => item.isFree)[0];
    const filterResponse =
      metaDataJSON && data?.address
        ? metaDataJSON.find((x) => x.address.toLowerCase() === data.address.toLowerCase())
        : undefined;
    return {
      ...data,
      placeholderData: {
        name: formatAddress(filterResponse?.address ?? '', data?.tokenId),
        image: filterResponse?.image
      }
    };
  }, [metaDataList, metaDataJSON]);

  const showDiscount: SelectedCollectionProps[] = useMemo(() => {
    const data = metaDataList.filter((item) => item.isDiscount);
    const result = getUniqueDiscounts(data);
    const updatedList = result.map((item) => {
      const matchingItem =
        metaDataJSON && item?.address
          ? metaDataJSON.find((x) => x.address.toLowerCase() === item.address.toLowerCase())
          : undefined;
      if (matchingItem) {
        const placeholderData = {
          name: formatAddress(item?.address ?? '', item?.tokenId),
          image: matchingItem.image
        };

        return {
          ...item,
          placeholderData
        };
      } else {
        return item;
      }
    });

    return updatedList;
  }, [metaDataList, metaDataJSON]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedCollection(e.target.value);
      const collectionIndex = parseInt(e.target.value, 10);
      const collectionDetails = collectionIndex === 1 ? showFree : showDiscount[collectionIndex - 2];

      setSelectedCollectionData((prev) => ({
        ...prev,
        address: collectionDetails.address,
        discountPercentage: collectionDetails.discountPercentage,
        image: collectionDetails.image,
        isDiscount: collectionDetails.isDiscount,
        isFree: collectionDetails.isFree,
        name: collectionDetails.name,
        tokenId: collectionDetails.tokenId
      }));
    },
    [setSelectedCollection, showFree, showDiscount, setSelectedCollectionData]
  );

  const onError = useCallback((event: string) => {
    if (event === 'free') {
      setIsErrorFree(true);
    }
    if (event === 'discount') {
      setIsErrorDiscount(true);
    }
    if (event === 'discountTwo') {
      setIsErrorDiscountTwo(true);
    }
  }, []);

  React.useEffect(() => {
    if (showFree?.isFree) {
      setSelectedCollectionData(showFree);
      setSelectedCollection('1');
    }
    if (showDiscount?.[0]?.isDiscount && !showFree?.isFree) {
      setSelectedCollectionData(showDiscount?.[0]);
      setSelectedCollection('2');
    }

    return () => {
      setIsErrorDiscount(false);
      setIsErrorFree(false);
      setIsErrorDiscountTwo(false);
    };
  }, [showFree, showDiscount, setSelectedCollectionData, setSelectedCollection]);

  return (
    <RadioGroup
      aria-label="chooseCollection"
      name="chooseCollection"
      value={selectedCollection}
      onChange={handleChange}
      sx={{
        marginTop: '20px'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexDirection: 'column'
        }}
      >
        {showFree?.isFree && (
          <FormControlLabel
            sx={{
              border: selectedCollection === '1' ? `2px solid ${theme.palette.primary.main}` : '2px solid #40464A',
              width: '100%',
              marginLeft: 0,
              marginRight: 0,
              display: 'flex',
              flexDirection: 'row-reverse',
              justifyContent: 'space-between',
              borderRadius: '5px'
            }}
            value="1"
            control={
              <Radio
                icon={selectedCollection === '1' ? <Radio /> : <></>}
                checkedIcon={
                  <CheckCircleIcon
                    sx={{
                      color: theme.palette.primary.main
                    }}
                  />
                }
              />
            }
            label={
              <Stack
                sx={{
                  padding: '16px'
                }}
                direction="row"
                gap={2}
              >
                <Image
                  onError={() => onError('free')}
                  src={(isErrorFree ? showFree?.placeholderData?.image : showFree.image) || ''}
                  style={{
                    borderRadius: '5px',
                    height: '100px'
                  }}
                  alt="collection-image"
                />
                <Stack justifyContent="center">
                  <Typography
                    sx={{
                      color: theme?.palette?.text?.primary,
                      fontSize: '16px',
                      fontWeight: 500,
                      marginBottom: '4px',
                      fontFamily: theme?.MojitoClaim?.font?.secondary
                    }}
                  >
                    {showFree.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: theme?.ClaimTemplate?.collectionHeaderColor,
                      fontSize: '14px',
                      fontWeight: 500,
                      fontFamily: theme?.MojitoClaim?.font?.secondary
                    }}
                  >
                    Free
                  </Typography>
                  <Typography
                    sx={{
                      color: theme?.ClaimTemplate?.collectionHeaderColor,
                      fontSize: '14px',
                      fontWeight: 500,
                      fontFamily: theme?.MojitoClaim?.font?.secondary
                    }}
                  >
                    {showFree?.count || 0} unused
                  </Typography>
                </Stack>
              </Stack>
            }
          />
        )}
        {showDiscount?.[0] && (
          <FormControlLabel
            key={showDiscount?.[0].address}
            value="2"
            sx={{
              border: selectedCollection === '2' ? `2px solid ${theme.palette.primary.main}` : '2px solid #40464A',
              width: '100%',
              marginLeft: 0,
              marginRight: 0,
              display: 'flex',
              flexDirection: 'row-reverse',
              justifyContent: 'space-between',
              borderRadius: '5px'
            }}
            control={
              <Radio
                icon={selectedCollection === '2' ? <Radio /> : <></>}
                checkedIcon={
                  <CheckCircleIcon
                    sx={{
                      color: theme.palette.primary.main
                    }}
                  />
                }
              />
            }
            label={
              <Stack
                sx={{
                  padding: '16px'
                }}
                direction="row"
                gap={2}
              >
                <Image
                  onError={() => onError('discount')}
                  src={(isErrorDiscount ? showDiscount?.[0]?.placeholderData?.image : showDiscount?.[0]?.image) || ''}
                  style={{
                    borderRadius: '5px',
                    height: '100px'
                  }}
                  alt="collection-image"
                />
                <Stack justifyContent="center">
                  <Typography
                    sx={{
                      color: theme?.palette?.text?.primary,
                      fontSize: '16px',
                      fontWeight: 500,
                      marginBottom: '4px',
                      fontFamily: theme?.MojitoClaim?.font?.secondary
                    }}
                  >
                    {showDiscount?.[0].name}
                  </Typography>
                  <Typography
                    sx={{
                      color: theme?.ClaimTemplate?.collectionHeaderColor,
                      fontSize: '14px',
                      fontWeight: 500,
                      fontFamily: theme?.MojitoClaim?.font?.secondary
                    }}
                  >
                    {showDiscount?.[0].discountPercentage}% Discount
                  </Typography>
                  <Typography
                    sx={{
                      color: theme?.ClaimTemplate?.collectionHeaderColor,
                      fontSize: '14px',
                      fontWeight: 500,
                      fontFamily: theme?.MojitoClaim?.font?.secondary
                    }}
                  >
                    {showDiscount?.[0]?.count || 0} unused
                  </Typography>
                </Stack>
              </Stack>
            }
          />
        )}
        {showDiscount?.[1] && (
          <FormControlLabel
            key={showDiscount?.[1].address}
            value="3"
            sx={{
              border: selectedCollection === '3' ? `2px solid ${theme.palette.primary.main}` : '2px solid #40464A',
              width: '100%',
              marginLeft: 0,
              marginRight: 0,
              display: 'flex',
              flexDirection: 'row-reverse',
              justifyContent: 'space-between',
              borderRadius: '5px'
            }}
            control={
              <Radio
                icon={selectedCollection === '3' ? <Radio /> : <></>}
                checkedIcon={
                  <CheckCircleIcon
                    sx={{
                      color: theme.palette.primary.main
                    }}
                  />
                }
              />
            }
            label={
              <Stack
                sx={{
                  padding: '16px'
                }}
                direction="row"
                gap={2}
              >
                <Image
                  onError={() => onError('discountTwo')}
                  src={
                    (isErrorDiscountTwo ? showDiscount?.[1]?.placeholderData?.image : showDiscount?.[1]?.image) || ''
                  }
                  style={{
                    borderRadius: '5px',
                    height: '100px'
                  }}
                  alt="collection-image"
                />
                <Stack justifyContent="center">
                  <Typography
                    sx={{
                      color: theme?.palette?.text?.primary,
                      fontSize: '16px',
                      fontWeight: 500,
                      marginBottom: '4px',
                      fontFamily: theme?.MojitoClaim?.font?.secondary
                    }}
                  >
                    {showDiscount?.[1].name}
                  </Typography>
                  <Typography
                    sx={{
                      color: theme?.ClaimTemplate?.collectionHeaderColor,
                      fontSize: '14px',
                      fontWeight: 500,
                      fontFamily: theme?.MojitoClaim?.font?.secondary
                    }}
                  >
                    {showDiscount?.[1].discountPercentage}% Discount
                  </Typography>
                  <Typography
                    sx={{
                      color: theme?.ClaimTemplate?.collectionHeaderColor,
                      fontSize: '14px',
                      fontWeight: 500,
                      fontFamily: theme?.MojitoClaim?.font?.secondary
                    }}
                  >
                    {showDiscount?.[1]?.count || 0} unused
                  </Typography>
                </Stack>
              </Stack>
            }
          />
        )}
      </Box>
    </RadioGroup>
  );
};

export default ChooseCollection;
